import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { http, formatError } from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { CalendarCheck2, Camera, Receipt, Users, Home as HomeIcon, Plus, Send, Copy, CheckCircle2, Repeat } from "lucide-react";
import { toast } from "sonner";
import PeaceOfMindReports from "@/pages/parent/PeaceOfMindReports";
import SubscriptionsTab from "@/pages/parent/SubscriptionsTab";
import VisitActions from "@/components/VisitActions";

export default function ParentDashboard() {
  const { user } = useAuth();
  const [students, setStudents] = useState([]);
  const [properties, setProperties] = useState([]);
  const [bookings, setBookings] = useState([]);
  const [appts, setAppts] = useState([]);
  const [invoices, setInvoices] = useState([]);
  const [reports, setReports] = useState([]);

  useEffect(() => { load(); }, []);
  function load() {
    Promise.all([
      http.get("/students"), http.get("/properties"), http.get("/bookings"),
      http.get("/appointments"), http.get("/invoices"), http.get("/reports"),
    ]).then(([s, p, b, a, i, r]) => {
      setStudents(s.data); setProperties(p.data); setBookings(b.data);
      setAppts(a.data); setInvoices(i.data); setReports(r.data);
    }).catch(() => {});
  }

  const upcoming = appts.filter((a) => a.status === "scheduled").slice(0, 5);
  const totalSpent = invoices.reduce((s, i) => s + (i.amount || 0), 0);

  return (
    <div className="max-w-7xl mx-auto px-6 py-10">
      <div className="flex flex-wrap items-end justify-between gap-3">
        <div>
          <Badge variant="outline">Parent portal</Badge>
          <h1 className="font-display text-3xl sm:text-4xl font-bold text-primary mt-2" data-testid="parent-dashboard-title">Welcome back, {user?.name?.split(" ")[0]}</h1>
          <p className="text-muted-foreground text-sm mt-1">Everything you need to keep your student's place spotless.</p>
        </div>
        <Button asChild className="bg-primary hover:bg-primary/90" data-testid="parent-new-booking-btn"><Link to="/booking"><Plus size={14} className="mr-1" /> New booking</Link></Button>
      </div>

      <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4 mt-8">
        <Stat icon={<CalendarCheck2 />} label="Upcoming cleanings" value={upcoming.length} />
        <Stat icon={<Users />} label="Students" value={students.length} />
        <Stat icon={<HomeIcon />} label="Properties" value={properties.length} />
        <Stat icon={<Receipt />} label="Total spent" value={`$${totalSpent.toFixed(0)}`} />
      </div>

      <Tabs defaultValue="upcoming" className="mt-10">
        <TabsList data-testid="parent-tabs">
          <TabsTrigger value="upcoming" data-testid="tab-upcoming">Upcoming</TabsTrigger>
          <TabsTrigger value="subscriptions" data-testid="tab-subscriptions"><Repeat size={12} className="mr-1" /> Plans</TabsTrigger>
          <TabsTrigger value="students" data-testid="tab-students">Students</TabsTrigger>
          <TabsTrigger value="properties" data-testid="tab-properties">Properties</TabsTrigger>
          <TabsTrigger value="reports" data-testid="tab-reports">Reports</TabsTrigger>
          <TabsTrigger value="invoices" data-testid="tab-invoices">Invoices</TabsTrigger>
        </TabsList>

        <TabsContent value="upcoming" className="mt-4">
          {upcoming.length === 0 ? (
            <Empty msg="No cleanings scheduled yet." cta={<Button asChild className="mt-3 bg-primary hover:bg-primary/90"><Link to="/booking">Book one now</Link></Button>} />
          ) : (
            <div className="grid md:grid-cols-2 gap-4">
              {upcoming.map((a) => (
                <Card key={a.id} className="border-border/60">
                  <CardContent className="p-5">
                    <div className="flex items-start justify-between gap-3">
                      <div>
                        <div className="font-display font-semibold text-primary">{a.property?.name || "Property"}</div>
                        <div className="text-xs text-muted-foreground">{a.student?.name}</div>
                      </div>
                      <Badge className="bg-secondary/10 text-secondary border border-secondary/20">{a.plan}</Badge>
                    </div>
                    <div className="mt-3 text-sm">📅 {a.scheduled_date}</div>
                    <div className="text-xs text-muted-foreground">{a.special_instructions || ""}</div>
                    <VisitActions visit={a} onChange={load} />
                  </CardContent>
                </Card>
              ))}
            </div>
          )}
        </TabsContent>

        <TabsContent value="subscriptions" className="mt-4">
          <SubscriptionsTab />
        </TabsContent>

        <TabsContent value="students" className="mt-4">
          {students.length === 0 ? <Empty msg="No students added yet." /> : (
            <div className="grid md:grid-cols-2 gap-4">
              {students.map((s) => (
                <Card key={s.id} className="border-border/60"><CardContent className="p-5">
                  <div className="flex items-start justify-between gap-3">
                    <div>
                      <div className="font-display font-semibold text-primary">{s.name}</div>
                      <div className="text-xs text-muted-foreground">{s.school || "—"}</div>
                      <div className="text-xs text-muted-foreground mt-1">{s.email || "no email on file"} {s.phone && ` · ${s.phone}`}</div>
                    </div>
                    <InviteStatusBadge status={s.invite_status} />
                  </div>
                  {s.invite_status !== "claimed" && (
                    <div className="mt-3">
                      <InviteButton student={s} />
                    </div>
                  )}
                </CardContent></Card>
              ))}
            </div>
          )}
        </TabsContent>

        <TabsContent value="properties" className="mt-4">
          {properties.length === 0 ? <Empty msg="No properties added yet." /> : (
            <div className="grid md:grid-cols-2 gap-4">
              {properties.map((p) => (
                <Card key={p.id} className="border-border/60"><CardContent className="p-5">
                  <div className="flex justify-between items-start">
                    <div className="font-display font-semibold text-primary">{p.name}</div>
                    <Badge variant="outline">{p.type}</Badge>
                  </div>
                  <div className="text-sm mt-2">{p.address}</div>
                  <div className="text-xs text-muted-foreground">{[p.building, p.unit, p.room_number && `Room ${p.room_number}`].filter(Boolean).join(" · ")}</div>
                </CardContent></Card>
              ))}
            </div>
          )}
        </TabsContent>

        <TabsContent value="reports" className="mt-4">
          <PeaceOfMindReports />
        </TabsContent>

        <TabsContent value="invoices" className="mt-4">
          {invoices.length === 0 ? <Empty msg="No invoices yet." /> : (
            <Card><CardContent className="p-0">
              <table className="w-full text-sm">
                <thead className="bg-muted/40 text-xs uppercase tracking-wider text-muted-foreground">
                  <tr><th className="text-left p-3">Date</th><th className="text-left p-3">Booking</th><th className="text-right p-3">Amount</th><th className="text-right p-3">Status</th></tr>
                </thead>
                <tbody>
                  {invoices.map((i) => (
                    <tr key={i.id} className="border-t border-border/60"><td className="p-3">{i.created_at?.slice(0, 10)}</td><td className="p-3 text-muted-foreground">{i.booking_id?.slice(0, 8)}…</td><td className="p-3 text-right font-semibold">${i.amount?.toFixed(2)}</td><td className="p-3 text-right"><Badge className="bg-secondary/10 text-secondary">{i.status}</Badge></td></tr>
                  ))}
                </tbody>
              </table>
            </CardContent></Card>
          )}
        </TabsContent>
      </Tabs>
    </div>
  );
}

function Stat({ icon, label, value }) {
  return (
    <Card className="border-border/60"><CardContent className="p-5">
      <div className="flex items-center justify-between">
        <div className="w-10 h-10 rounded-xl bg-primary/5 text-primary flex items-center justify-center">{icon}</div>
        <div className="font-display text-2xl font-bold text-primary">{value}</div>
      </div>
      <div className="text-xs text-muted-foreground mt-2 uppercase tracking-wider">{label}</div>
    </CardContent></Card>
  );
}

function Empty({ msg, cta }) {
  return (
    <Card className="border-dashed border-border"><CardContent className="p-10 text-center">
      <div className="text-muted-foreground">{msg}</div>
      {cta}
    </CardContent></Card>
  );
}

function InviteStatusBadge({ status }) {
  if (status === "claimed") return <Badge className="bg-secondary/10 text-secondary border border-secondary/20"><CheckCircle2 size={12} className="mr-1" /> Joined</Badge>;
  if (status === "pending") return <Badge variant="outline" className="text-muted-foreground">Invite pending</Badge>;
  return <Badge variant="outline">Not invited</Badge>;
}

function InviteButton({ student }) {
  const [open, setOpen] = useState(false);
  const [busy, setBusy] = useState(false);
  const [link, setLink] = useState("");
  const [copied, setCopied] = useState(false);

  async function send() {
    setBusy(true);
    try {
      const { data } = await http.post(`/students/${student.id}/invite`);
      const url = `${window.location.origin}/invite/${data.token}`;
      setLink(url);
      toast.success(`Invite link generated for ${data.email}`);
    } catch (e) {
      toast.error(formatError(e.response?.data?.detail) || "Could not generate invite");
    } finally { setBusy(false); }
  }

  function copyLink() {
    navigator.clipboard.writeText(link);
    setCopied(true);
    setTimeout(() => setCopied(false), 1800);
  }

  return (
    <Dialog open={open} onOpenChange={(v) => { setOpen(v); if (!v) setLink(""); }}>
      <DialogTrigger asChild>
        <Button size="sm" variant="outline" className="border-secondary/40 text-secondary hover:bg-secondary/5" data-testid={`invite-btn-${student.id}`}>
          <Send size={13} className="mr-1.5" /> {student.invite_status === "pending" ? "Resend invite" : "Invite student"}
        </Button>
      </DialogTrigger>
      <DialogContent className="max-w-md">
        <DialogHeader><DialogTitle>Invite {student.name}</DialogTitle></DialogHeader>
        {!student.email ? (
          <div className="text-sm text-muted-foreground">Add an email address for this student first, then come back here to send an invite.</div>
        ) : !link ? (
          <div className="space-y-3 text-sm">
            <p className="text-muted-foreground">We'll generate a private magic link tied to <b className="text-primary">{student.email}</b>. Send it however you'd like — text, email, or AirDrop. The link is valid for 7 days.</p>
            <Button onClick={send} disabled={busy} className="w-full bg-primary hover:bg-primary/90" data-testid={`invite-generate-${student.id}`}>
              {busy ? "Generating…" : "Generate invite link"}
            </Button>
          </div>
        ) : (
          <div className="space-y-3 text-sm">
            <p className="text-muted-foreground">Share this link with your student. When they click it, they'll set a password and land directly in their portal.</p>
            <div className="p-3 rounded-lg bg-muted/40 border border-border/60 text-xs break-all font-mono text-primary" data-testid={`invite-link-${student.id}`}>{link}</div>
            <Button onClick={copyLink} variant="outline" className="w-full" data-testid={`invite-copy-${student.id}`}>
              <Copy size={14} className="mr-1.5" /> {copied ? "Copied!" : "Copy link"}
            </Button>
            <p className="text-[11px] text-muted-foreground">Billing always stays with you — your student will only see their own cleanings, notes, and access info.</p>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}
